// ================================================================
// pages/login/Login.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, SET_CURRENT_USER, SET_LOGIN_STATUS } from '../../hooks/auth/AuthContext';
import './Login.less';
import { SIGN_IN_MUTATION } from '../../shared/graphql/auth/auth.gql';
import { useCookies } from 'react-cookie';
import { useMutation } from '@apollo/client';
import { authClient } from '../../apollo-clients';
import Modal from '../../shared/components/modal/Modal';

interface LoginProps {
  onLoginSuccess?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
    const { authState, authDispatch } = useAuth();
    const [userName, setUserName] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [cookies, setCookie] = useCookies();    
    const isProd = process.env.NODE_ENV === "production";
    const aToken = process.env.REACT_APP_ACCESS_TOKEN || 'sc-at';
    const rToken = process.env.REACT_APP_REFRESH_TOKEN || 'sc-rt';

    // Password authentication mutation
    const [signIn] = useMutation(SIGN_IN_MUTATION, { 
        client: authClient, 
        errorPolicy: 'all' 
    });

    // Clear error when component mounts
    useEffect(() => {
        setError('');
    }, []);

    const handleUserNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserName(e.target.value);
        if (error) {
            setError('');
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(e.target.value);
        if (error) {
            setError('');
        }
    };

    const handlePasswordLogin = async (userName: string, password: string) => {
        try {
            console.log('Starting password authentication...');
            
            // Authenticate with backend using userName and password
            console.log('Sending credentials to backend...');
            const { data, errors } = await signIn({
                variables: {
                    input: { userName, password }
                }
            });

            console.log('Backend response:', data);
            console.log('Backend errors:', errors);

            if (data?.signIn?.success) {
                console.log('Backend authentication successful');
                
                // Store tokens in cookies with proper settings
                const accessToken = data.signIn.accessToken;
                const refreshToken = data.signIn.refreshToken;
                
                console.log('Storing tokens in cookies...');
                
                setCookie(aToken, accessToken, {
                    path: "/",
                    domain: isProd ? window.location.hostname : "localhost",
                    maxAge: 86400, // 24 hours
                    secure: isProd,
                    sameSite: 'lax'
                });
                
                setCookie(rToken, refreshToken, {
                    path: "/",
                    domain: isProd ? window.location.hostname : "localhost", 
                    maxAge: 86400 * 7, // 7 days
                    secure: isProd,
                    sameSite: 'lax'
                });
                
                // Update auth context with the user data from backend
                authDispatch({ 
                    type: SET_CURRENT_USER, 
                    payload: { currentUser: data.signIn.user }
                });
                authDispatch({ type: SET_LOGIN_STATUS, payload: true });
                
                console.log('Auth context updated');
                return data.signIn;
            } else {
                const errorMsg = data?.signIn?.message || 'Authentication failed';
                console.error('Backend auth failed:', errorMsg);
                throw new Error(errorMsg);
            }
        } catch (error: any) {
            console.error('Login error:', error);
            throw new Error(error.message || 'Login failed');
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            await handlePasswordLogin(userName, password);
            onLoginSuccess?.();
        } catch (error: any) {
            setError(error.message);
            console.error('Login failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // If user is already logged in, show user info
    if (authState.isLogin && authState.userProfile) {
        return (
            <div className="login-container">
                <div className="login-card">
                    <div className="login-header">
                        <h2>Welcome Back!</h2>
                        <p>Hello, {authState.userProfile.name}!</p>
                        <p>Email: {authState.userProfile.email}</p>
                        <p>Role: {authState.userProfile.role}</p>
                    </div>
                    
                    {authState.sideNav.length > 0 && (
                        <div className="user-navigation">
                            <h3>Available Pages:</h3>
                            <ul>
                                {authState.sideNav.map((nav) => (
                                    <li key={nav.id}>
                                        <a href={nav.route} className={`nav-item permission-${nav.roleType.toLowerCase()}`}>
                                            {nav.icon && <i className={nav.icon}></i>}
                                            <span>{nav.name}</span>
                                            <small>({nav.roleType})</small>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h2>Welcome Back</h2>
                    <p>Sign in to your account</p>
                </div>

                <form onSubmit={handleLogin} className="login-form">
                    <div className="form-group">
                        <label htmlFor="userName">Username</label>
                        <input
                            type="text"
                            id="userName"
                            value={userName}
                            onChange={handleUserNameChange}
                            placeholder="Enter your username (e.g., TJ123456)"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={handlePasswordChange}
                            placeholder="Enter your password"
                            required
                            disabled={loading}
                        />
                    </div>



                    <button 
                        type="submit" 
                        className="login-btn primary"
                        disabled={loading}
                    >
                        {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>
                <div className="login-footer">
                    <p>Don't have an account? <Link to="/signup">Sign up</Link></p>
                </div>
                {error && (
                    <Modal
                        message={error}
                        success={false}
                        onClose={() => setError('')}
                        showCancelButton={false}
                    />
                )}
            </div>
        </div>
    );
};

export default Login;
